import { Component, For, createMemo } from "solid-js";
import type { ProposedAction } from "../../api/types";
import { isQueryTool } from "./groupActions";

interface ImpactChip {
  label: string;
  variant: "neutral" | "create" | "destructive";
}

function computeChips(actions: ProposedAction[]): ImpactChip[] {
  const counts = {
    creates: 0,
    deletes: 0,
    statusChanges: 0,
    priorityUpdates: 0,
    updates: 0,
    documents: 0,
  };

  for (const action of actions) {
    if (isQueryTool(action.tool_name)) continue;
    switch (action.tool_name) {
      case "create_work_item":
      case "create_child_item":
        counts.creates++;
        break;
      case "delete_work_item":
        counts.deletes++;
        break;
      case "change_work_item_status":
        counts.statusChanges++;
        break;
      case "set_priority": {
        counts.priorityUpdates++;
        break;
      }
      case "update_work_item": {
        const params = action.user_modification || action.parameters;
        if ("priority" in params) counts.priorityUpdates++;
        else counts.updates++;
        break;
      }
      case "create_document":
      case "update_document":
      case "publish_document":
      case "archive_document":
        counts.documents++;
        break;
      default:
        counts.updates++;
    }
  }

  const chips: ImpactChip[] = [];
  if (counts.statusChanges > 0)
    chips.push({
      label: `${counts.statusChanges} status change${counts.statusChanges !== 1 ? "s" : ""}`,
      variant: "neutral",
    });
  if (counts.priorityUpdates > 0)
    chips.push({
      label: `${counts.priorityUpdates} priority update${counts.priorityUpdates !== 1 ? "s" : ""}`,
      variant: "neutral",
    });
  if (counts.updates > 0)
    chips.push({
      label: `${counts.updates} update${counts.updates !== 1 ? "s" : ""}`,
      variant: "neutral",
    });
  if (counts.creates > 0)
    chips.push({
      label: `${counts.creates} new item${counts.creates !== 1 ? "s" : ""}`,
      variant: "create",
    });
  if (counts.documents > 0)
    chips.push({
      label: `${counts.documents} document${counts.documents !== 1 ? "s" : ""}`,
      variant: "neutral",
    });
  if (counts.deletes > 0)
    chips.push({
      label: `${counts.deletes} deletion${counts.deletes !== 1 ? "s" : ""}`,
      variant: "destructive",
    });
  return chips;
}

const chipClasses: Record<ImpactChip["variant"], string> = {
  neutral: "bg-sunken text-ink-2",
  create: "bg-signal-green-tint text-signal-green",
  destructive: "bg-signal-red-tint text-signal-red font-medium",
};

export interface ProposalImpactSummaryProps {
  actions: ProposedAction[];
}

export const ProposalImpactSummary: Component<ProposalImpactSummaryProps> = (
  props,
) => {
  const chips = createMemo(() => computeChips(props.actions));

  return (
    <div
      class="flex flex-wrap gap-1.5"
      role="region"
      aria-label="Proposal impact summary"
    >
      <For each={chips()}>
        {(chip) => (
          <span
            class={
              "inline-flex items-center px-2 py-0.5 rounded-full text-xs " +
              chipClasses[chip.variant]
            }
          >
            {chip.label}
          </span>
        )}
      </For>
    </div>
  );
};

export default ProposalImpactSummary;
